import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { Subject } from "rxjs";
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { HttpService } from "../http/http";
import { Auth } from "../auth";
import { catchError, last, map, takeUntil } from "rxjs/operators";
import { environment } from "../../../../environments/environment";

export class OracleOciUploadFile implements IUploadFile {
  get isComplete(): boolean {
    return this._isComplete;
  }

  private _abortedChange: Subject<void>;

  get abortedChange(): Subject<void> {
    return this._abortedChange;
  }

  private _isAborted: boolean;

  get isAborted(): boolean {
    return this._isAborted;
  }

  private _isCompleteChange: Subject<any>;

  get isCompleteChange(): Subject<any> {
    return this._isCompleteChange;
  }

  private _isError: boolean;

  get isError(): boolean {
    return this._isError;
  }

  private _isErrorChange: Subject<void>;

  get isErrorChange(): Subject<void> {
    return this._isErrorChange;
  }

  private _progress: number;

  get progress(): number {
    return this._progress;
  }

  private _progressChange: Subject<number>;

  get progressChange(): Subject<number> {
    return this._progressChange;
  }

  get customData(): any {
    return this._customData;
  }

  get id() {
    return this._uploadId;
  }

  get file() {
    return this._file;
  }

  get createdDate() {
    return this._createdDate;
  }

  protected _cancelUploadRequest: Subject<void> = new Subject<void>();
  private _started = false;
  private _duringAbort = false;
  private _fileUrl: string;
  private _fileAccessUrl: string;

  private _uploadParts: Array<UploadPart>;
  private _isComplete: boolean;

  constructor(
    private _uploadId: string,
    private _file: File,
    private _fileType: string,
    private _customData: any,
    private notifyPromise: (objectKey: string) => Promise<any>,
    private _createdDate: Date,
    private _httpClient: HttpClient,
    private _httpService: HttpService,
    private _auth: Auth,
  ) {
    this._abortedChange = new Subject<void>();
    this._isErrorChange = new Subject<any>();
    this._progressChange = new Subject<number>();
    this._isCompleteChange = new Subject<any>();
  }

  async start() {
    if (this._started) {
      return;
    }

    this._started = true;
    this.setProgress(0);

    //1. create pre-authenticated url
    const createPreAuthenticatedUrlResponse =
      await this._createPreAuthenticatedUrl(this._file.name, this._fileType);
    this._fileUrl = createPreAuthenticatedUrlResponse.fileUrl;

    if (!this._uploadParts) {
      this._uploadParts = await this.createUploadPartsArray(
        createPreAuthenticatedUrlResponse.chunkSize,
      );
    }

    //2. create multiple-parts upload request
    const startMultiPartUploadResponse =
      await this._startMultiPartUploadRequest(
        createPreAuthenticatedUrlResponse,
      );

    this._fileAccessUrl =
      createPreAuthenticatedUrlResponse.endpointUrl +
      `/${startMultiPartUploadResponse.accessUri}`;
    //3. create and start promises for every part
    const multiPartUploadTasks = this._createMultiPartUploadTasks(
      createPreAuthenticatedUrlResponse,
      startMultiPartUploadResponse,
      this._uploadParts,
    );

    //4. wait until all promises are finished
    /*for (let i = 0; i < multiPartUploadPromises.length; i+= this._concurrentDownloadConnections) {
      const runningTasks = multiPartUploadPromises.splice(i, this._concurrentDownloadConnections);
      await Promise.all(runningTasks.map(t => t.execute()));
    }*/
    const tempActiveTasks = [];
    while (multiPartUploadTasks.length > 0) {
      tempActiveTasks.splice(0, tempActiveTasks.length);
      tempActiveTasks.push(
        ...multiPartUploadTasks.splice(
          0,
          createPreAuthenticatedUrlResponse.concurrentConnections,
        ),
      );
      await Promise.all(tempActiveTasks.map((t) => t.execute()));
    }

    //5. send complete multi-part upload request
    await this._finishMultiPartUploadRequest(
      createPreAuthenticatedUrlResponse,
      startMultiPartUploadResponse,
    );

    console.log("setComplete ");
    this.setComplete(this._fileUrl);
  }

  async abort() {
    if (this._duringAbort) {
      return;
    }
    this._duringAbort = true;
    console.log(
      `OracleUploadFile-> Abort uploading ${this._fileUrl} . Deleting artifacts...`,
    );
    this._cancelUploadRequest.next();
    this._cancelUploadRequest.complete();

    this._cancelUploadRequest = new Subject<void>();

    await this._httpClient.delete(this._fileAccessUrl, null).toPromise();

    this.setAbort();
  }

  async retry() {
    console.log(`OracleUploadFile-> Retry uploading ${this._fileUrl}`);
    this._started = false;
    return this.start();
  }

  private _createMultiPartUploadTasks(
    preAuthenticatedResponse: ICratePreauthenticatedUrlResponse,
    startMultiPartUploadResponse: IStartMultiPartUploadResponse,
    parts: UploadPart[],
  ): PendingPromise<void>[] {
    const result = [];
    this._cancelUploadRequest = new Subject<void>();
    let bytesUploaded = 0;
    parts.forEach((part) => {
      result.push(
        new PendingPromise<void>(part.partNumber, (resolve) => {
          const req = new HttpRequest(
            "PUT",
            preAuthenticatedResponse.endpointUrl +
              `${startMultiPartUploadResponse.accessUri}${part.partNumber}`,
            part.blob,
            {
              reportProgress: true,
            },
          );
          this._httpClient
            .request(req)
            .pipe(
              takeUntil(this._cancelUploadRequest),
              catchError((err, observer) => {
                this.setError(err.message);
                return err;
              }),
            )
            .subscribe((event: HttpEvent<string>) => {
              if (event.type === HttpEventType.UploadProgress) {
                part.bytesUploaded = event.loaded;
                const percentDone =
                  parts
                    .map((p) => p.bytesUploaded)
                    .reduce((partialSum, a) => partialSum + a, 0) /
                  this._file.size;
                this.setProgress(percentDone);
                console.log(
                  `OracleUploadFile->part->${part.partNumber}->File is ${percentDone}% uploaded.`,
                  bytesUploaded,
                  this._file.size,
                  event.loaded,
                  event.total,
                );
              }
              if (event.type === HttpEventType.Response) {
                console.log(
                  `OracleUploadFile->${part.partNumber}->donwload completed`,
                );
                resolve();
              }
            });
        }),
      );
    });
    return result;
  }
  private _startMultiPartUploadRequest(
    cratePreauthenticatedUrlResponse: ICratePreauthenticatedUrlResponse,
  ): Promise<IStartMultiPartUploadResponse> {
    return this._httpClient
      .put(cratePreauthenticatedUrlResponse.preAuthenticatedUrl, null, {
        headers: { "opc-multipart": "true" },
      })
      .toPromise()
      .then((result: IStartMultiPartUploadResponse) => result);
  }

  private _finishMultiPartUploadRequest(
    cratePreauthenticatedUrlResponse: ICratePreauthenticatedUrlResponse,
    startMultiPartUploadResponse: IStartMultiPartUploadResponse,
  ): Promise<Object> {
    return this._httpClient
      .post(
        cratePreauthenticatedUrlResponse.endpointUrl +
          `${startMultiPartUploadResponse.accessUri}`,
        null,
      )
      .toPromise();
  }

  private createUploadPartsArray(
    partSizeInBytes: number,
  ): Promise<Array<UploadPart>> {
    return new Promise((resolve, reject) => {
      const result = [];

      for (
        let i = 0, partNum = 1;
        i < this._file.size;
        i += partSizeInBytes, partNum++
      ) {
        const bytesSlice = this._file.slice(i, i + partSizeInBytes);
        const blob = new Blob([bytesSlice], {
          type: "application/octet-stream",
        });
        result.push(new UploadPart(partNum, blob));
      }

      resolve(result);
    });
  }

  private setComplete(notifyServerResult: any) {
    this._isComplete = true;
    this.notifyPromise(notifyServerResult);
    this._isCompleteChange.next(notifyServerResult);
  }

  private setError(err) {
    this._isError = true;
    this._isErrorChange.next(err);
  }

  private setProgress(val) {
    this._progress = val;
    this._progressChange.next(val);
  }

  private setAbort() {
    this._isAborted = true;
    this._abortedChange.next();
  }

  private _createPreAuthenticatedUrl(
    fileName: string,
    fileType: string,
  ): Promise<ICratePreauthenticatedUrlResponse> {
    return this._httpService.get(
      `OracleCloud/CreatePreauthenticatedUrl?fileName=${fileName}&fileType=${fileType}`,
    );
    //return Promise.resolve(<ICratePreauthenticatedUrlResponse>{endpointUrl: 'https://objectstorage.eu-frankfurt-1.oraclecloud.com', preAuthenticatedUrl: 'https://objectstorage.eu-frankfurt-1.oraclecloud.com/p/vmeH6udYMV7wUCOAIZ2lVrMgFMd_8smDBZVhQI4OGrIMdx0tUqPT3BIT3Cgy2F0R/n/axcxgqlbvhjy/b/Originals/o/alexey-test-sdk/video-file-2.mp4', fileUrl: 'https://axcxgqlbvhjy.compat.objectstorage.eu-frankfurt-1.oraclecloud.com/Originals/alexey-test-sdk/video-file-2.mp4'});
  }
}
interface IStartMultiPartUploadResponse {
  namespace: string;
  bucket: string;
  object: string;
  uploadId: string;
  timeCreated: string;
  storageTier: string;
  accessUri: string;
}
interface ICratePreauthenticatedUrlResponse {
  preAuthenticatedUrl: string;
  fileUrl: string;
  chunkSize: number;
  concurrentConnections: number;
  endpointUrl: string;
}
interface ISegmentPath {
  internalContainerPath: string;
  segmentUrl: string;
}

class SegmentMetaData {
  constructor(
    public path: string,
    public etag: string,
    public size_bytes: number,
  ) {}
}
class UploadPart {
  constructor(
    public partNumber: number,
    public blob: Blob,
    public bytesUploaded = 0,
  ) {}
}

class PendingPromise<PromiseType> {
  constructor(
    public taskId,
    private _args,
  ) {}

  execute() {
    return new Promise<PromiseType>(this._args);
  }
}
